import { FC, useMemo } from 'react';
import { Order } from 'src/trpc';
import { safeMoneyFromCents } from '@libs/services/money';
import { ContainerBreakdown } from '@libs/schemas';

interface Props {
  order: Order;
  finalWeight?: number;
  additionalMaterials?: { [materialId: string]: number };
  allAvailableMaterials?: { _id: string; extraChargeInCents: number; name?: string }[];
  containerBreakdown?: ContainerBreakdown[];
}

export const FixedChargeSummary: FC<Props> = ({
  order,
  finalWeight = 0,
  additionalMaterials = {},
  allAvailableMaterials = [],
  containerBreakdown = [],
}) => {
  // Extract fixed pricing details from quotedPricingDetails
  const fixedDetails = useMemo(() => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

    return {
      includedDays: containerPricing?.includedDays || 0,
      dailyOverageFeeInCents: containerPricing?.dailyOverageFeeInCents || 0,
      perTonRateInCents: materialPricing?.perTonRateInCents || order.pricePerTonInCents || 0,
    };
  }, [order.quotedPricingDetails, order.pricePerTonInCents]);

  // Calculate weight cost (all weight in fixed pricing incurs cost)
  const weightCost = finalWeight * fixedDetails.perTonRateInCents;

  return (
    <>
      {/* Weight Adjustments - In fixed pricing, all weight incurs cost */}
      {finalWeight > 0 && (
        <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
          <h6>Additional Weight: {finalWeight.toFixed(2)} ton(s)</h6>
          <span>{safeMoneyFromCents(weightCost).toFormat()}</span>
        </div>
      )}

      {/* Container Service Adjustments */}
      {containerBreakdown.map((breakdown) => {
        return (
          <div key={breakdown.containerId} className="flex flex-col gap-1">
            <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
              <h6>
                {breakdown.containerName} Dumpster: {breakdown.usedDays} days total
              </h6>
            </div>
            <div className="flex flex-row justify-between w-full font-light text-xs text-muted pl-2">
              <h6>Included: {breakdown.includedDays} days</h6>
              <span>{safeMoneyFromCents(0).toFormat()}</span>
            </div>
            {breakdown.extraDaysPaid > 0 && (
              <div className="flex flex-row justify-between w-full font-light text-xs text-muted pl-2">
                <h6>Extra days prepaid: {breakdown.extraDaysPaid} days</h6>
                <span>{safeMoneyFromCents(0).toFormat()}</span>
              </div>
            )}
            {breakdown.refundDays > 0 && (
              <div className="flex flex-row justify-between w-full font-light text-xs text-muted pl-2">
                <h6>Refund: {breakdown.refundDays} days</h6>
                <span>- {safeMoneyFromCents(breakdown.refundAmountInCents).toFormat()}</span>
              </div>
            )}
            {breakdown.additionalDays > 0 && (
              <div className="flex flex-row justify-between w-full font-light text-xs text-muted pl-2">
                <h6>Additional: {breakdown.additionalDays} days</h6>
                <span>{safeMoneyFromCents(breakdown.additionalCostInCents).toFormat()}</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Additional Materials */}
      {allAvailableMaterials &&
        Object.keys(additionalMaterials).map((materialId) => {
          const material = allAvailableMaterials.find((m) => m._id === materialId);
          const quantity = additionalMaterials[materialId] || 0;
          const cost = quantity * (material?.extraChargeInCents || 0);
          if (quantity === 0) return null;
          return (
            <div
              className="flex flex-row justify-between w-full font-light text-xs text-muted"
              key={materialId}
            >
              <h6>{material?.name || 'Unknown Material'}</h6>
              <span>{safeMoneyFromCents(cost).toFormat()}</span>
            </div>
          );
        })}
    </>
  );
};